import React, { useState } from 'react';
import {
  HStack,
  VStack,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Checkbox,
  Badge,
  Text,
} from '@chakra-ui/react';
import { FaDollarSign, FaArrowUp, FaArrowRight, FaArrowDown, FaBan, FaHistory } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import { throwUnlessApiSuccess } from '../utils/apiResponse';
import { applyRecipientBalancePatch } from '../utils/applyRecipientBalancePatch';
import BalanceTransferModal from './BalanceTransferModal';
import PlayerHistoryModal from './PlayerHistoryModal';
import { updatePlayer } from '../actions/playerActions';
import { usePrivilege } from '../hooks/usePrivilege';
import { getLoggedInUserRole } from '../utils/auth';
import { useAppToast } from '../hooks/useAppToast';

const OBJECT_ID_RE = /^[a-fA-F0-9]{24}$/;

/** Report rows nest Mongo id under playerInfo; player docs use _id directly. */
export const resolvePlayerMongoId = (player) => {
  if (!player) return null;
  const nestedId = player.playerInfo?._id;
  if (nestedId) return String(nestedId);
  const id = player._id;
  if (id && OBJECT_ID_RE.test(String(id))) return String(id);
  return null;
};

const closedSettingsModal = { isOpen: false, initial: null, values: null };

const toInputValue = (value) => (value == null ? '' : String(value));

const readPlayerSettings = (player) => {
  const info = player.playerInfo || {};
  const pick = (key) => info[key] ?? player[key];
  return {
    targetPerc: toInputValue(player.target_perc ?? info.target_perc),
    allowedWin: toInputValue(pick('allowed_win')),
    negativeWinLimit: toInputValue(pick('negative_win_limit') ?? -1000),
    giveManualWin: Boolean(pick('give_manual_win')),
    highLimit: Number(pick('high_limit')) > 0 ? String(pick('high_limit')) : '',
    manualNtp: String(pick('manual_ntp') || 'AUTO').toUpperCase(),
  };
};

const PlayerActionButtons = ({ player, refresh }) => {
  const dispatch = useDispatch();
  const { showSuccess, showWarning, showApiError } = useAppToast();
  const { can } = usePrivilege();
  const isSuperAdmin = getLoggedInUserRole() === 'super_admin';
  const [isTransferOpen, setIsTransferOpen] = useState(false);
  const [transferDetails, setTransferDetails] = useState({});
  const [settingsModal, setSettingsModal] = useState(closedSettingsModal);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  const canSetTarget = can('player.setTarget');
  const canSetAllowedWin = can('player.setAllowedWin');
  const canSetNegativeWinLimit = can('player.setNegativeWinLimit');
  const canGiveManualWin = can('player.giveManualWin');
  const canSetHighLimit = can('player.setHighLimit');
  const canSetNtp = can('player.setNtp');
  const canEditSettings = canSetTarget || canSetAllowedWin || canSetNegativeWinLimit || canGiveManualWin || canSetHighLimit;
  const canOpenSettings = canEditSettings || canSetNtp;

  const playerMongoId = resolvePlayerMongoId(player);
  const transferToId = player.playerInfo?.username || player.username || player._id;

  const handleOpenTransferModal = (toRole, toId, recipientBalance, recipientBalanceCheck) => {
    setTransferDetails({ toRole, toId, recipientBalance, recipientBalanceCheck });
    setIsTransferOpen(true);
  };

  const handleBalanceTransfer = async ({ fromRole, fromId, toRole, toId, amount }) => {
    const response = await axios.post(`${process.env.REACT_APP_API_URL_ADMIN}`, {
      apiName: 'user',
      command: 'transferBalance',
      fromRole,
      fromId,
      toRole,
      toId,
      amount,
    });
    const data = throwUnlessApiSuccess(response);
    if (refresh) refresh();
    return data;
  };

  const handleUpdate = async (payload, message) => {
    if (!playerMongoId) {
      showApiError(new Error('Player id not found for this row'), 'Player id not found for this row');
      return false;
    }
    try {
      await dispatch(updatePlayer(playerMongoId, payload));
      if (refresh) refresh();
      showSuccess(message);
      return true;
    } catch (error) {
      showApiError(error, 'Failed to update player');
      return false;
    }
  };

  const openSettingsModal = () => {
    const initial = readPlayerSettings(player);
    setSettingsModal({ isOpen: true, initial, values: { ...initial } });
  };

  const closeSettingsModal = () => setSettingsModal(closedSettingsModal);

  const setSettingsValue = (key, value) => {
    setSettingsModal((prev) => ({ ...prev, values: { ...prev.values, [key]: value } }));
  };

  const handleSetAuto = async () => {
    const ok = await handleUpdate({ manual_ntp: 'AUTO' }, 'NTP set to AUTO');
    if (ok) {
      setSettingsModal((prev) => ({
        ...prev,
        initial: { ...prev.initial, manualNtp: 'AUTO' },
        values: { ...prev.values, manualNtp: 'AUTO' },
      }));
    }
  };

  const handleSaveSettings = async () => {
    const { initial, values } = settingsModal;
    const payload = {};

    if (canSetTarget && values.targetPerc !== initial.targetPerc) {
      const perc = parseFloat(values.targetPerc);
      if (Number.isNaN(perc)) {
        showWarning('Enter a valid percentage');
        return;
      }
      if (!isSuperAdmin && (perc < 0 || perc > 100)) {
        showWarning('Target percentage must be between 0 and 100');
        return;
      }
      payload.target_perc = perc;
      payload.targetNTP = perc;
      payload.manualBlock = true;
    }

    if (canSetAllowedWin && values.allowedWin !== initial.allowedWin) {
      const allowedWin = Number(values.allowedWin);
      if (values.allowedWin === '' || Number.isNaN(allowedWin) || allowedWin < 0) {
        showWarning('Allowed Win must be a number ≥ 0');
        return;
      }
      payload.allowed_win = allowedWin;
    }

    if (canSetNegativeWinLimit && values.negativeWinLimit !== initial.negativeWinLimit) {
      const negativeWinLimit = Number(values.negativeWinLimit);
      if (values.negativeWinLimit === '' || !Number.isFinite(negativeWinLimit)) {
        showWarning('Negative Win Limit must be a finite number');
        return;
      }
      payload.negative_win_limit = negativeWinLimit;
    }

    if (canGiveManualWin && values.giveManualWin !== initial.giveManualWin) {
      payload.give_manual_win = values.giveManualWin;
    }

    if (canSetHighLimit && values.highLimit !== initial.highLimit) {
      const highLimit = values.highLimit === '' ? 0 : Number(values.highLimit);
      if (Number.isNaN(highLimit) || highLimit < 0) {
        showWarning('High Limit must be a number ≥ 0');
        return;
      }
      payload.high_limit = highLimit;
    }

    if (Object.keys(payload).length === 0) {
      showWarning('No changes to save');
      return;
    }

    const ok = await handleUpdate(payload, 'Player settings updated');
    if (ok) closeSettingsModal();
  };

  const settingsValues = settingsModal.values;
  const isAuto = !settingsValues || settingsValues.manualNtp === 'AUTO';

  return (
    <>
      <HStack spacing={2}>
        {can('player.transfer') && (
        <Button
          size="sm"
          colorScheme="teal"
          onClick={() => handleOpenTransferModal(
            'player',
            transferToId,
            player.balance ?? player.playerInfo?.balance,
            player.balance_check ?? player.playerInfo?.balance_check
          )}
        >
          <FaDollarSign />
        </Button>
        )}
        {canSetNtp && (
        <Button size="sm" onClick={() => handleUpdate({ manual_ntp: 'HIGH', manualBlock: true }, 'NTP set to HIGH')}><FaArrowUp /></Button>
        )}
        {canOpenSettings && (
        <Button size="sm" onClick={openSettingsModal}>
          <FaArrowRight />
        </Button>
        )}
        {canSetNtp && (
        <Button size="sm" onClick={() => handleUpdate({ manual_ntp: 'LOWEST', manualBlock: true }, 'NTP set to LOWEST')}><FaArrowDown /></Button>
        )}
        {can('player.block') && (
        <Button size="sm" onClick={() => handleUpdate({ is_active: false }, 'Player Deactivated')}><FaBan /></Button>
        )}
        {can('report.activityLog') && (
        <Button size="sm" title="History" aria-label="History" onClick={() => setIsHistoryOpen(true)}><FaHistory /></Button>
        )}
      </HStack>

      <PlayerHistoryModal
        isOpen={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        username={player.playerInfo?.username || player.username}
        playerId={playerMongoId}
      />

      <BalanceTransferModal
        isOpen={isTransferOpen}
        onClose={() => setIsTransferOpen(false)}
        toRole={transferDetails.toRole}
        toId={transferDetails.toId}
        recipientBalance={transferDetails.recipientBalance}
        recipientBalanceCheck={transferDetails.recipientBalanceCheck}
        onTransfer={handleBalanceTransfer}
        onTransferSuccess={(r) => applyRecipientBalancePatch(dispatch, r)}
      />

      <Modal isOpen={settingsModal.isOpen} onClose={closeSettingsModal} scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Player Settings — {transferToId}</ModalHeader>
          <ModalCloseButton />
          {settingsValues && (
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {canSetNtp && (
              <HStack justify="space-between">
                <HStack>
                  <Text fontWeight="medium">Manual NTP:</Text>
                  <Badge colorScheme={isAuto ? 'green' : 'orange'}>{settingsValues.manualNtp}</Badge>
                </HStack>
                <Button size="sm" colorScheme="green" isDisabled={isAuto} onClick={handleSetAuto}>
                  AUTO
                </Button>
              </HStack>
              )}
              {canSetTarget && (
              <FormControl>
                <FormLabel>Target Percentage</FormLabel>
                <Input
                  type="number"
                  min={isSuperAdmin ? undefined : 0}
                  max={isSuperAdmin ? undefined : 100}
                  step="any"
                  value={settingsValues.targetPerc}
                  onChange={(e) => setSettingsValue('targetPerc', e.target.value)}
                />
              </FormControl>
              )}
              {canSetAllowedWin && (
              <FormControl>
                <FormLabel>Allowed Win</FormLabel>
                <Input
                  type="number"
                  min={0}
                  step="any"
                  value={settingsValues.allowedWin}
                  onChange={(e) => setSettingsValue('allowedWin', e.target.value)}
                />
              </FormControl>
              )}
              {canSetHighLimit && (
              <FormControl>
                <FormLabel>High Limit</FormLabel>
                <Input
                  type="number"
                  min={0}
                  step="any"
                  placeholder="Empty = normal HIGH"
                  value={settingsValues.highLimit}
                  onChange={(e) => setSettingsValue('highLimit', e.target.value)}
                />
                <FormHelperText>
                  While HIGH, pays the largest win up to this amount, even past this player&apos;s own negative-win cap.
                </FormHelperText>
              </FormControl>
              )}
              {canSetNegativeWinLimit && (
              <FormControl>
                <FormLabel>Negative Win Limit</FormLabel>
                <Input
                  type="number"
                  step="any"
                  value={settingsValues.negativeWinLimit}
                  onChange={(e) => setSettingsValue('negativeWinLimit', e.target.value)}
                />
              </FormControl>
              )}
              {canGiveManualWin && (
              <FormControl>
                <Checkbox
                  isChecked={settingsValues.giveManualWin}
                  onChange={(e) => setSettingsValue('giveManualWin', e.target.checked)}
                >
                  Give Manual Win
                </Checkbox>
                <FormHelperText>
                  Pays the largest win within Allowed Win, even past this player&apos;s own negative-win cap. Cleared after the win.
                </FormHelperText>
              </FormControl>
              )}
            </VStack>
          </ModalBody>
          )}
          <ModalFooter>
            {canEditSettings && (
            <Button colorScheme="blue" mr={2} onClick={handleSaveSettings}>
              Save
            </Button>
            )}
            <Button variant="ghost" onClick={closeSettingsModal}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default PlayerActionButtons;
